import {createRow, type RowModel, type Table} from '@tanstack/react-table'

import type {MemexItemModel} from '../../../models/memex-item-model'
import type {SubIssuesByParentId} from '../../../state-providers/memex-items/queries/use-paginated-memex-items-query'
import {getRowId} from '../helpers/get-row-id'
import type {Row} from '../helpers/types'

/**
 * Creates nested sub-rows for a given parent row. Handles both regular sub-issues
 * and agent task sub-issues as unified nested content.
 *
 * When `copilotTasksEnabled` is true, sub-issues with any truthy `agentTaskState`
 * are sorted to the top and marked with `defaultVisible: true` so they render
 * without requiring the parent to be expanded.
 *
 * @param parentRow The parent row for which to get nested sub-rows
 * @param subIssuesByParentId A mapping of parent issue IDs to their corresponding sub-issue items
 * @param table The TanStack Table instance
 * @param rowModel The TanStack Table row model
 * @param options.copilotTasksEnabled When true, items with agentTaskState are marked as defaultVisible
 * @param visitedParentIds Tracks visited parent content IDs during recursion to prevent infinite loops from circular hierarchies
 *
 * @returns An array of nested sub-rows for the given parent row
 */

export const getSubIssueSubRows = (
  parentRow: Row<MemexItemModel>,
  subIssuesByParentId: SubIssuesByParentId,
  table: Table<MemexItemModel>,
  rowModel: RowModel<MemexItemModel>,
  options?: {copilotTasksEnabled?: boolean},
  visitedParentIds: Set<string | number> = new Set(),
) => {
  const parentContentId = parentRow.original.content.id

  // Defensive guard against circular references in the sub-issues hierarchy.
  // The server prevents circular dependencies at creation time, but this protects
  // against edge cases in cached or stale data.
  if (visitedParentIds.has(parentContentId)) return []
  visitedParentIds.add(parentContentId)

  // Get sub-issue items available from the query cache by the row issue's id.
  const data = subIssuesByParentId[parentContentId]
  // If not loaded, return empty array.
  if (!data) return []

  // Initialize custom subIssueData row model property if not set.
  if (!parentRow.subIssueData) {
    parentRow.subIssueData = {subIssueDepth: 0}
  }

  const {subIssues: allItems, missingSubIssues} = data
  // When copilot tasks are enabled, sort items with agentTaskState to the top
  // so they float above regular sub-issues in the nested content.
  const items = options?.copilotTasksEnabled
    ? [...allItems].sort((a, b) => {
        const aIsTask = a.agentTaskState ? 0 : 1
        const bIsTask = b.agentTaskState ? 0 : 1
        return aIsTask - bIsTask
      })
    : allItems
  // Sub-issue depth is separate from row depth to account for grouping.
  // Parent subIssueDepth should be defined at this point since it is set above, this is just to appease typechecking.
  const subIssueDepth = (parentRow.subIssueData?.subIssueDepth ?? -1) + 1
  const subIssues = items.map((item, index) => {
    // Create a row id that is unique within the context of the row's ancestors.
    // Built recursively in the format of `${parentRowId}-${itemId}`.
    const newRowId = getRowId(item, index, {id: parentRow.id})
    const newRow = createRow<MemexItemModel>(table, newRowId, item, index, parentRow.depth + 1, undefined, parentRow.id)
    const isAgentTask = options?.copilotTasksEnabled && !!item.agentTaskState
    newRow.subIssueData = {
      subIssueDepth,
      defaultVisible: isAgentTask,
    }

    rowModel.flatRows.push(newRow)
    // Recursively get sub-rows for the new row.
    newRow.subRows = getSubIssueSubRows(newRow, subIssuesByParentId, table, rowModel, options, visitedParentIds)
    rowModel.rowsById[newRow.id] = newRow

    return newRow
  })

  // If the parent issue has missing sub-issues, add a custom row to represent them.
  // Sub-issues are considered missing if they are not in the project.
  if (missingSubIssues && missingSubIssues.length > 0) {
    const missingRow = createRow<MemexItemModel>(
      table,
      getRowId({id: 'missing'}, subIssues.length, {id: parentRow.id}),
      parentRow.original,
      subIssues.length,
      parentRow.depth + 1,
      undefined,
      parentRow.id,
    )
    missingRow.subIssueData = {
      subIssueDepth,
      missingSubIssues,
    }
    rowModel.flatRows.push(missingRow)
    rowModel.rowsById[missingRow.id] = missingRow
    subIssues.push(missingRow)
  }

  return subIssues
}
